/**
 * A level, as a slider and a button that plays what it sets.
 *
 * The range slider's twin, with the one thing a volume control cannot do without. Every other knob
 * demonstrates itself the moment it is dragged, because whatever it changes is on the screen behind
 * the settings panel. A volume slider dragged in a quiet menu demonstrates nothing, and two of the
 * four buses have nothing on them to demonstrate even in a firefight.
 *
 * Not built on top of `rangeSlider`. A control kind owns its own DOM, and the alternative was
 * exporting the slider's internals so a second kind could wrap them, which is a larger seam than
 * the twenty lines it would save. The coercion is shared, because that half is a rule rather than a
 * layout and two copies of a clamp is how two controls come to disagree about their own bounds.
 */

import { el, setText } from '@/ui/dom';
import { clamp } from '@/sim/math';
import { SETTING_AUDITION } from '@/text/settingsText';
import type {
  SettingControl,
  SettingControlDefinition,
  SettingControlHandle,
  SettingControlOptions,
  SettingValue,
} from '@/ui/settings/settingContract';

export const LEVEL_SLIDER: SettingControlDefinition = {
  kind: 'level',

  coerce(control: SettingControl, raw: unknown): SettingValue | null {
    if (control.kind !== 'level') return null;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
    return clamp(raw, control.min, control.max);
  },

  create(options: SettingControlOptions): SettingControlHandle {
    const control = options.definition.control;
    // The registry resolved this control by its own kind, so nothing else can arrive here. An
    // empty handle rather than a throw: a settings screen missing one row beats one that is blank.
    if (control.kind !== 'level') return { root: el('div', 'settings-control'), dispose: noop };

    const root = el('div', 'settings-control');
    const start = typeof options.value === 'number' ? options.value : control.min;

    const slider = el('input', 'settings-slider');
    slider.type = 'range';
    slider.min = String(control.min);
    slider.max = String(control.max);
    slider.step = String(control.step);
    slider.value = String(start);
    slider.ariaLabel = options.definition.label;

    const readout = el('output', 'settings-readout', control.format(start));

    const audition = el('button', 'settings-audition', SETTING_AUDITION);
    audition.type = 'button';

    const current = (): number => clamp(slider.valueAsNumber, control.min, control.max);

    const onInput = (): void => {
      const value = current();
      setText(readout, control.format(value));
      options.onChange(value);
    };
    const onCommit = (): void => {
      options.onCommit(current());
    };
    const onAudition = (): void => {
      // Committed first, so the tone is heard through the value the character will keep rather
      // than through one a drag left applied and unsaved.
      options.onCommit(current());
      control.audition();
    };

    slider.addEventListener('input', onInput);
    slider.addEventListener('change', onCommit);
    audition.addEventListener('click', onAudition);

    root.append(slider, readout, audition);

    return {
      root,
      dispose(): void {
        slider.removeEventListener('input', onInput);
        slider.removeEventListener('change', onCommit);
        audition.removeEventListener('click', onAudition);
        root.remove();
      },
    };
  },
};

/** What the unreachable branch in `create` hands back, so a missing row disposes like any other. */
function noop(): void {
  return undefined;
}
